/**
 * WCC360 v2 — "Your career matches" chapter.
 *
 * Two stated-interest screens, both scored, both bounded:
 *
 *   1. CHILDHOOD DREAM (`px_childhood_dream`) — "When you were about 10,
 *      what did you want to be?" This is safe to ask directly because it
 *      is a memory, not a virtue: there is no socially correct answer to
 *      "I wanted to be an astronaut". It is the weakest signal in the walk
 *      by design (`MAX_DREAM_NUDGE`, half a passion pick) — a ten-year-old's
 *      answer is a hint about what pulls at someone, not a plan.
 *
 *   2. CAREER PULL (`px_career_pull`) — up to three worlds of work the
 *      learner wants to see more of. Same machinery as passions: an
 *      archetype nudge plus a family-affinity bonus.
 *
 * Both routes feed the SAME bounded channels passions already use — the
 * archetype tally nudge and the family-affinity strength map — so no new
 * weight enters the six scoring slots and no eligibility gate ever reads
 * them. A stated interest can put work in front of you. It can never
 * promote a genuine misfit or delete a career from the pool.
 *
 * CANON-PENDING: X-DREAM-001 and X-PULL-001 are not yet ratified in the
 * Item Bank. Recorded here until the Command Center carries them.
 */
import { ARCHETYPES, emptyVector, type ArchetypeVector, type Archetype } from "./archetypes";

/** Total archetype points the childhood dream may contribute. */
export const MAX_DREAM_NUDGE = 1;
/** Total archetype points the career-pull picks may contribute, in total. */
export const MAX_PULL_NUDGE = 2;

export const MAX_PULLS = 3;
export const NO_DREAM_ID = "cant_remember";

export interface InterestOption {
  id: string;
  label: string;
  hint?: string;
  archetypeWeights: Partial<Record<Archetype, number>>;
  families: string[];
}

/**
 * X-DREAM-001. Deliberately the childhood job titles people actually name,
 * not adult career families — the point is recall, not classification.
 */
export const CHILDHOOD_DREAMS: InterestOption[] = [
  {
    id: "doctor",
    label: "A doctor or a nurse",
    hint: "Fixing people",
    archetypeWeights: { Caregiver: 3, Thinker: 1 },
    families: ["Healthcare & Medicine", "Health & Social Care", "Pharmaceuticals & Biotech"],
  },
  {
    id: "teacher",
    label: "A teacher",
    hint: "Explaining things to people",
    archetypeWeights: { Caregiver: 2, Connector: 2 },
    families: ["Education & Training", "Educational Technology (EdTech)"],
  },
  {
    id: "astronaut",
    label: "An astronaut or a pilot",
    hint: "Going somewhere nobody's been",
    archetypeWeights: { Builder: 2, Thinker: 2 },
    families: ["Space & Aerospace", "Transportation & Logistics", "Military & Defense"],
  },
  {
    id: "engineer",
    label: "An engineer or an inventor",
    hint: "Making machines that work",
    archetypeWeights: { Builder: 3, Thinker: 1 },
    families: ["Engineering (Non-IT)", "Engineering", "Manufacturing & Industrial"],
  },
  {
    id: "scientist",
    label: "A scientist",
    hint: "Finding out why",
    archetypeWeights: { Thinker: 3 },
    families: ["Science & Research", "Research & Development", "Earth & Environmental Science"],
  },
  {
    id: "artist",
    label: "An artist, writer or musician",
    hint: "Making something out of nothing",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media", "Design & User Experience", "Arts, Culture & Heritage"],
  },
  {
    id: "athlete",
    label: "A sportsperson",
    hint: "Competing, training, winning",
    archetypeWeights: { Builder: 2, Leader: 1 },
    families: ["Sports & Fitness", "Sports & Recreation"],
  },
  {
    id: "police",
    label: "Police, army or a firefighter",
    hint: "Keeping people safe",
    archetypeWeights: { Advocate: 2, Builder: 1, Leader: 1 },
    families: ["Military & Defense", "Public Service & Safety", "Government & Public Sector"],
  },
  {
    id: "vet",
    label: "A vet, or someone who works with animals",
    hint: "Looking after animals",
    archetypeWeights: { Caregiver: 3 },
    families: ["Animal Care & Welfare", "Agriculture & Food Production", "Agriculture & Environment"],
  },
  {
    id: "business",
    label: "Someone with their own business",
    hint: "Running the show",
    archetypeWeights: { Leader: 2, Organizer: 1, Connector: 1 },
    families: ["Entrepreneurship & Startups", "Sales & Business Development", "Business & Administration"],
  },
  {
    id: "helper",
    label: "Someone who helps people who are struggling",
    hint: "Standing up for people",
    archetypeWeights: { Advocate: 3, Caregiver: 1 },
    families: ["Social Work & NGO", "Legal & Compliance", "Personal & Community Services"],
  },
  {
    id: NO_DREAM_ID,
    label: "I genuinely can't remember",
    hint: "Completely normal — nothing is scored here",
    archetypeWeights: {},
    families: [],
  },
];

/**
 * X-PULL-001. Twelve worlds of work, phrased as the work itself and never
 * as a job title, so a learner isn't picking a label they half-recognise.
 */
export const CAREER_PULLS: InterestOption[] = [
  {
    id: "tech",
    label: "Building software and digital things",
    archetypeWeights: { Thinker: 2, Builder: 2 },
    families: ["Technology & Software", "AI & Emerging Technologies", "Cybersecurity", "Product Management"],
  },
  {
    id: "data",
    label: "Working out what the numbers are saying",
    archetypeWeights: { Thinker: 3 },
    families: ["Data & Analytics", "Data, AI & Analytics", "Finance & Accounting"],
  },
  {
    id: "health",
    label: "Looking after people's health",
    archetypeWeights: { Caregiver: 3 },
    families: ["Healthcare & Medicine", "Health & Social Care", "Pharmaceuticals & Biotech"],
  },
  {
    id: "teaching",
    label: "Teaching, coaching or mentoring",
    archetypeWeights: { Caregiver: 2, Connector: 2 },
    families: ["Education & Training", "Coaching & Professional Development", "Educational Technology (EdTech)"],
  },
  {
    id: "creative",
    label: "Making things people watch, read or listen to",
    archetypeWeights: { Creator: 3 },
    families: ["Creative Arts & Media", "Journalism & Media", "Creator Economy & Digital", "Design & User Experience"],
  },
  {
    id: "hands",
    label: "Building and fixing real, physical things",
    archetypeWeights: { Builder: 3 },
    families: ["Engineering (Non-IT)", "Blue-Collar & Trade", "Skilled Trades & Construction", "Manufacturing & Industrial"],
  },
  {
    id: "business",
    label: "Growing a business or selling an idea",
    archetypeWeights: { Leader: 2, Connector: 2 },
    families: ["Sales & Business Development", "Marketing & Growth", "Entrepreneurship & Startups", "Consulting & Strategy"],
  },
  {
    id: "money",
    label: "Money, markets and how they move",
    archetypeWeights: { Thinker: 2, Organizer: 2 },
    families: ["Finance & Accounting", "FinTech & Digital Finance", "Banking", "Economics & Policy"],
  },
  {
    id: "planet",
    label: "The climate and the natural world",
    archetypeWeights: { Advocate: 2, Thinker: 1 },
    families: ["Green Tech & Climate Action", "Energy & Environment", "Agriculture & Food Production", "Agriculture & Environment"],
  },
  {
    id: "justice",
    label: "Law, rights and public service",
    archetypeWeights: { Advocate: 3, Organizer: 1 },
    families: ["Legal & Compliance", "Government & Public Sector", "Public Service & Safety", "Social Work & NGO"],
  },
  {
    id: "people",
    label: "Bringing people together and running things well",
    archetypeWeights: { Organizer: 2, Connector: 2 },
    families: ["Human Resources", "Hospitality & Tourism", "Business & Administration", "Transportation & Logistics"],
  },
  {
    id: "science",
    label: "Research, labs and figuring out the unknown",
    archetypeWeights: { Thinker: 3 },
    families: ["Science & Research", "Research & Development", "Space & Aerospace", "Semiconductors & Electronics"],
  },
];

function byId(list: InterestOption[], id: string | undefined): InterestOption | undefined {
  return id ? list.find((o) => o.id === id) : undefined;
}

export function dreamById(id: string | undefined) { return byId(CHILDHOOD_DREAMS, id); }
export function pullById(id: string | undefined) { return byId(CAREER_PULLS, id); }

export function selectedPulls(ids: string[] | undefined): InterestOption[] {
  if (!ids?.length) return [];
  return ids.map((id) => pullById(id)).filter((o): o is InterestOption => Boolean(o));
}

/** Normalise a raw archetype vector so it contributes at most `cap` points. */
function capped(raw: ArchetypeVector, cap: number): ArchetypeVector {
  const total = ARCHETYPES.reduce((s, a) => s + (raw[a] ?? 0), 0);
  if (total <= 0) return emptyVector();
  const out = emptyVector();
  for (const a of ARCHETYPES) out[a] = ((raw[a] ?? 0) / total) * cap;
  return out;
}

function vectorFor(options: InterestOption[]): ArchetypeVector {
  const raw = emptyVector();
  for (const o of options) {
    for (const [k, w] of Object.entries(o.archetypeWeights) as [Archetype, number][]) {
      if (w) raw[k] = (raw[k] ?? 0) + w;
    }
  }
  return raw;
}

/** Childhood dream → archetype nudge, capped at `MAX_DREAM_NUDGE`. */
export function dreamArchetypeNudge(id: string | undefined): ArchetypeVector {
  const dream = dreamById(id);
  if (!dream || dream.id === NO_DREAM_ID) return emptyVector();
  return capped(vectorFor([dream]), MAX_DREAM_NUDGE);
}

/** Career-pull picks → archetype nudge, capped at `MAX_PULL_NUDGE` in total. */
export function pullArchetypeNudge(ids: string[] | undefined): ArchetypeVector {
  const picks = selectedPulls(ids);
  if (picks.length === 0) return emptyVector();
  return capped(vectorFor(picks), MAX_PULL_NUDGE);
}

/**
 * Family-affinity counts from both screens. A pull pick counts fully; the
 * childhood dream counts as half, so a memory alone cannot reach the
 * "named it twice" strong bonus.
 */
export function interestFamilyCounts(
  dreamId: string | undefined,
  pullIds: string[] | undefined,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const o of selectedPulls(pullIds)) {
    for (const f of o.families) out.set(f, (out.get(f) ?? 0) + 1);
  }
  const dream = dreamById(dreamId);
  if (dream && dream.id !== NO_DREAM_ID) {
    for (const f of dream.families) out.set(f, (out.get(f) ?? 0) + 0.5);
  }
  return out;
}

/** Merge two family-strength maps (passions + this chapter). */
export function mergeFamilyCounts(
  ...maps: ReadonlyMap<string, number>[]
): Map<string, number> {
  const out = new Map<string, number>();
  for (const m of maps) {
    for (const [k, v] of m) out.set(k, (out.get(k) ?? 0) + v);
  }
  return out;
}

/** Reason line for the result card, when the pull is what surfaced it. */
export function pullReason(
  pullIds: string[] | undefined,
  family: string,
): string | null {
  const hit = selectedPulls(pullIds).find((o) => o.families.includes(family));
  return hit ? `you said you wanted to see more of "${hit.label.toLowerCase()}"` : null;
}
